import React, { createContext, useState, useEffect } from 'react';
import { getAuthTokens , getAuthEmail, setAuthToken, setAuthEmail, clearAuthToken, clearAuthEmail, clearAuthInitials, clearSelectedRole,getSelectedRole, setSelectedRole } from '../utils/auth';
import { appLogout, getUserContext , lo} from '../services/apiservice';
import { azureADB2CAuthentication } from '../services/apiservice';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

export const AuthContext = createContext({
  user: null,
  setUser: () => {}, 
  login: () => {},
  logout: () => {},
  avatarColor: null,
  initials: null,
  isAuthenticated: false,
  loading: true,
  resetPassword: () => {},
  setIsPasswordResetSuccessful: (value) => {},
  isPasswordResetSuccessful: false,
  clearPasswordResetState: () => {},
  onSwitchRole: () => {}
});

const getColorForRole = (role) => {
  switch (role) {
    case 'Issuer':
      return '#3EA4B4';
    case 'Admin':
      return '#3EB489';
    default:
      return '#6C757D';
  }
};


export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPasswordResetSuccessful, setIsPasswordResetSuccessful] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [avatarColor, setAvatarColor] = useState(null);
  const [initials, setInitials] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const removeRolePrefix = (path) => {
    return path.replace(/^\/(admin|issuer|user)/i, '');
  };
  
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      const { authToken, refreshToken, } = getAuthTokens();
      const selectedRole = getSelectedRole()?.selectedRole || null;

      if (authToken && refreshToken) {
        try {
          const userDetails = await getUserContext(authToken, selectedRole);
          const userContext = userDetails.data.userContext;
    
          if (!userContext.UserIsVerified) {
            navigate("/access-denied");
            setLoading(false);
            return;
          }
    
          const updatedUser = { ...userContext, role: selectedRole || userContext.roles[0]};
          setUser(updatedUser);
          setIsAuthenticated(true);
    
          const normalizedPath = removeRolePrefix(location.pathname);
          const expectedPath = `/${selectedRole.toLowerCase()}${normalizedPath}`;
    
          if (!location.pathname.startsWith(`/${selectedRole.toLowerCase()}`)) {
            navigate(expectedPath, { replace: true });
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          setIsAuthenticated(false);
          logout();
        }
      } else {
        const specialPaths = [
          "/assignment/approve/",
          "/users/",
          "/issuersignup?",
          "/signup?",
          "/password-reset-success",
          "/issuers/",
          "/company/",
          // "/choose-role",
          "/companyissuersignup?",
        ];
    
        const isSpecialPath = specialPaths.some((path) => location.pathname.startsWith(path));
    
        if (!isSpecialPath && !["/login", "/adminSignIn", "/adminSignup", "/issuersignup", "/companyissuersignup", "/signup", "/password-reset-success"].includes(location.pathname)) {
          navigate("/login", { replace: true });
        }
      }
      setLoading(false);
    };
    
    if (!user) {
      checkAuth();
    }
  }, [location.pathname, user]);
  

const getRolePath = (role) => {
  const rolePaths = {
    Admin: "/admin/",
    Issuer: "/issuer/",
    User: "/user/",
    CompanyAdmin: "/company-admin/", 
  };

  return rolePaths[role] || "/";
};


  useEffect(() => {
    const email = user?.role === 'Issuer' ? user?.IssuerRepEmailID : user?.UserEmail 
    const initials = email ? email.substring(0, 2).toUpperCase() : 'NA';
    const userRole = user?.role;
    const newAvatarColor = userRole ? getColorForRole(userRole) : '#6C757D';

    setInitials(initials);
    setAvatarColor(newAvatarColor);
  }, [isAuthenticated, user]);

  const onSwitchRole = async (newRole) => {
    if (!user || !user.roles.includes(newRole)) {
      console.error(`User does not have the role: ${newRole}`);
      return;
    }
  
    try {
      const { authToken } = getAuthTokens();
  
      if (!authToken) {
        console.error("Auth token not found. Redirecting to login.");
        logout();
        return;
      }
      const response = await getUserContext(authToken, newRole);
      if (response.success) {
        const userContext = response.data.userContext;
  
        if (!userContext.UserIsVerified) {
          navigate("/access-denied");
          return;
        }
        setSelectedRole(newRole)
        setUser({ ...userContext, role: newRole });
        const targetPath = getRolePath(newRole);
        navigate(targetPath, { replace: true });
      } else {
        console.error("Failed to fetch user context");
      }
    } catch (error) {
      console.error("Error fetching user context after switching role:", error);
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      const response = await azureADB2CAuthentication(email, password); 
      if (response.success) {
        const {accessToken, refreshToken} = response.data;
        setAuthToken(accessToken, refreshToken);
        const userDetails = await getUserContext(accessToken, null);
        const userContext = userDetails.data.userContext;
        
        if (!userContext.UserIsVerified) {
          navigate('/access-denied');
          setLoading(false);
          return;
        }
        const { roles, ...rest } = userContext;
        const selectedRole = roles.length > 0 ? roles[0] : null;

        const updatedUser = { ...userContext, role: selectedRole };
        setUser(updatedUser);
        setIsAuthenticated(true);
        setSelectedRole(selectedRole)
        const targetPath = getRolePath(selectedRole);
        if (location.pathname !== targetPath) {
          navigate(targetPath);
        }

      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };
  



  const resetPassword = () => {
    setIsPasswordResetSuccessful(true);
};

const clearPasswordResetState = () => {
    setIsPasswordResetSuccessful(false);
};

  const logout = async () => {
    try {
      clearAuthToken(); 
      clearAuthEmail();
      clearAuthInitials();
      setUser(null);
      setIsAuthenticated(false);
      setAvatarColor(null);
      setInitials(null);
      clearSelectedRole();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated,  setUser, user, loading, initials, avatarColor, login, logout , resetPassword, setIsPasswordResetSuccessful, isPasswordResetSuccessful, clearPasswordResetState, onSwitchRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);




// import React, { createContext, useState, useEffect } from 'react';
// import { getAuthTokens , getAuthEmail, setAuthToken, setAuthEmail, clearAuthToken, clearAuthEmail, clearAuthInitials, clearSelectedRole, setselectedRole, getselectedRole } from '../utils/auth';
// import { appLogout, getUserContext , lo} from '../services/apiservice';
// import { azureADB2CAuthentication } from '../services/apiservice';
// import { useNavigate, useLocation } from 'react-router-dom';
// import Cookies from 'js-cookie';

// export const AuthContext = createContext({
//   user: null,
//   setUser: () => {}, 
//   login: () => {},
//   logout: () => {},
//   avatarColor: null,
//   initials: null,
//   isAuthenticated: false,
//   loading: true,
//   resetPassword: () => {},
//   setIsPasswordResetSuccessful: (value) => {},
//   isPasswordResetSuccessful: false,
//   clearPasswordResetState: () => {},
//   onSwitchRole: () => {}
// });

// const getColorForRole = (role) => {
//   switch (role) {
//     case 'Issuer':
//       return '#3EA4B4';
//     case 'Admin':
//       return '#3EB489';
//     default:
//       return '#6C757D';
//   }
// };


// export const AuthProvider = ({ children }) => {
//   const [isAuthenticated, setIsAuthenticated] = useState(false);
//   const [isPasswordResetSuccessful, setIsPasswordResetSuccessful] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [user, setUser] = useState(null);
//   const [avatarColor, setAvatarColor] = useState(null);
//   const [initials, setInitials] = useState(null);
//   const navigate = useNavigate();
//   const location = useLocation();

//   const removeRolePrefix = (path) => {
//     return path.replace(/^\/(admin|issuer|user)/i, '');
//   };
  
//   useEffect(() => {
//     const checkAuth = async () => {
//       setLoading(true);
//       const { authToken, refreshToken } = getAuthTokens();
    
//       if (authToken && refreshToken) {
//         try {
//           const userDetails = await getUserContext(authToken);
//           const userContext = userDetails.data.userContext;
    
//           if (!userContext.UserIsVerified) {
//             navigate("/access-denied");
//             setLoading(false);
//             return;
//           }
    
//           let selectedRole = localStorage.getItem("selectedRole") || userContext.roles[0];
    
//           const updatedUser = { ...userContext, role: selectedRole };
//           setUser(updatedUser);
//           setIsAuthenticated(true);
    
//           const normalizedPath = removeRolePrefix(location.pathname);
//           const expectedPath = `/${selectedRole.toLowerCase()}${normalizedPath}`;
    
//           if (!location.pathname.startsWith(`/${selectedRole.toLowerCase()}`)) {
//             navigate(expectedPath, { replace: true });
//           }
//         } catch (error) {
//           console.error("Error fetching user details:", error);
//           setIsAuthenticated(false);
//           logout();
//         }
//       } else {
//         const specialPaths = [
//           "/assignment/approve/",
//           "/users/",
//           "/issuersignup?",
//           "/signup?",
//           "/password-reset-success",
//           "/issuers/",
//           "/company/",
//           "/select-role",
//           "/companyissuersignup?",
//         ];
    
//         const isSpecialPath = specialPaths.some((path) => location.pathname.startsWith(path));
    
//         if (!isSpecialPath && !["/login", "/adminSignIn", "/adminSignup", "/issuersignup", "/companyissuersignup", "/signup", "/password-reset-success"].includes(location.pathname)) {
//           navigate("/login", { replace: true });
//         }
//       }
//       setLoading(false);
//     };
    
//     if (!user) {
//       checkAuth();
//     }
//   }, [location.pathname, user]);
  

// const getRolePath = (role) => {
//   const rolePaths = {
//     Admin: "/admin/",
//     Issuer: "/issuer/",
//     User: "/user/",
//     CompanyAdmin: "/company-admin/", 
//   };

//   return rolePaths[role] || "/";
// };


//   useEffect(() => {
//     const email = user?.role === 'Issuer' ? user?.IssuerRepEmailID : user?.UserEmail 
//     const initials = email ? email.substring(0, 2).toUpperCase() : 'NA';
//     const userRole = user?.role;
//     const newAvatarColor = userRole ? getColorForRole(userRole) : '#6C757D';

//     setInitials(initials);
//     setAvatarColor(newAvatarColor);
//   }, [isAuthenticated, user]);

//   const onSwitchRole = (newRole) => {
//     if (!user || !user.roles.includes(newRole)) {
//       console.error(`User does not have the role: ${newRole}`);
//       return;
//     }
  
//     // Update the role in localStorage
//     localStorage.setItem("selectedRole", newRole);
  
//     // Update the user state in AuthContext
//     setUser((prevUser) => ({ ...prevUser, role: newRole }));
  
//     // Navigate to the correct path
//     const targetPath = getRolePath(newRole);
//     navigate(targetPath, { replace: true });
//   };
  

//   const login = async (email, password) => {
//     try {
//       const response = await azureADB2CAuthentication(email, password); 
//       if (response.success) {
//         const {accessToken, refreshToken} = response.data;
//         setAuthToken(accessToken, refreshToken);
//         const userDetails = await getUserContext(accessToken);
//         const userContext = userDetails.data.userContext;
        
//         if (!userContext.UserIsVerified) {
//           navigate('/access-denied');
//           setLoading(false);
//           return;
//         }
//         const { roles, ...rest } = userContext;
//         const selectedRole = roles.length > 0 ? roles[0] : null;

//         const updatedUser = { ...rest, role: selectedRole };
//         setUser(updatedUser);
//         setIsAuthenticated(true);
//           localStorage.setItem("selectedRole", selectedRole);
//           const targetPath = getRolePath(selectedRole);
//           if (location.pathname !== targetPath) {
//             navigate(targetPath);
//           }

//       } else {
//         throw new Error(response.message || 'Login failed');
//       }
//     } catch (error) {
//       console.error('Login failed:', error);
//       throw error;
//     }
//   };
  



//   const resetPassword = () => {
//     setIsPasswordResetSuccessful(true);
// };

// const clearPasswordResetState = () => {
//     setIsPasswordResetSuccessful(false);
// };

//   const logout = async () => {
//     try {
//       clearAuthToken(); 
//       clearAuthEmail();
//       clearAuthInitials();
//       setUser(null);
//       setIsAuthenticated(false);
//       setAvatarColor(null);
//       setInitials(null);
//       clearSelectedRole();
//       navigate('/login', { replace: true });
//     } catch (error) {
//       console.error('Logout failed:', error);
//     }
//   };

//   return (
//     <AuthContext.Provider value={{ isAuthenticated,  setUser, user, loading, initials, avatarColor, login, logout , resetPassword, setIsPasswordResetSuccessful, isPasswordResetSuccessful, clearPasswordResetState, onSwitchRole }}>
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export const useAuth = () => React.useContext(AuthContext);

